import {
  alertSeverityTone,
  alertToneLabel,
  anyAlertIsSevere,
  weatherAsOfLabel,
} from "../lib/weatherCopy";
import type { WeatherAlertOut } from "../types";

export interface WeatherAlertsBannerProps {
  alerts: WeatherAlertOut[];
  alertsFetchedAt: string | null;
  alertsStale: boolean;
}

/**
 * ADR-weather-map-provider-2026-07-10, keyless phase 1 (dashboard weather
 * card only) — SA Amendment 1 condition 1 (BINDING): "the Phase-1 alerts
 * banner... MUST render alerts independently of the forecast `available`
 * flag." This component renders purely off its own props; it has no
 * dependency whatsoever on `available`/`current`/forecast `stale` —
 * `DashboardView`'s `WeatherCard` mounts it ABOVE the loading/error/
 * unavailable/available branching so an active NWS alert still shows even
 * when the forecast itself is `available: false` (the QA case that
 * condition explicitly names).
 *
 * Severity -> tone (existing tokens only, no new ones): "warning"
 * (Extreme/Severe, `--clay`, `role="alert"` — assertive, interrupts
 * assistive tech) vs "watch" (everything else, `--amber-deep`,
 * `role="status"` — polite). Never color-alone: every alert also renders
 * its own visible tone-label text (`lib/weatherCopy.ts::alertToneLabel`).
 *
 * Stale alerts (`alertsStale`) get the SAME "as of ... may be a little
 * out of date" qualifier the forecast half already uses
 * (`weatherAsOfLabel`, reused verbatim here) — one staleness-copy
 * convention, not two.
 */
export function WeatherAlertsBanner({
  alerts,
  alertsFetchedAt,
  alertsStale,
}: WeatherAlertsBannerProps) {
  if (alerts.length === 0) return null;
  const severe = anyAlertIsSevere(alerts);

  return (
    <div
      class="weather-alerts-banner"
      role={severe ? "alert" : "status"}
      aria-label="Weather alerts for the farm"
    >
      <ul class="weather-alerts-list">
        {alerts.map((alert) => {
          const tone = alertSeverityTone(alert.severity);
          return (
            <li key={alert.id} class={`weather-alert weather-alert--${tone}`}>
              <span class={`weather-alert-tag weather-alert-tag--${tone}`}>
                {alertToneLabel(tone)}
              </span>
              <p class="weather-alert-event">{alert.event}</p>
              <p class="weather-alert-headline">{alert.headline}</p>
            </li>
          );
        })}
      </ul>
      {alertsFetchedAt ? (
        <p class="weather-alerts-asof">{weatherAsOfLabel(alertsFetchedAt, alertsStale)}</p>
      ) : null}
    </div>
  );
}
